/**
 * Toast Notification Component
 * Modern ES6 module for beautiful, user-friendly toast notifications
 * 
 * Features:
 * - Auto-dismiss with customizable duration
 * - Manual close with × button
 * - Smooth slide-in animations
 * - Mobile responsive design
 * - Multiple toast support
 * - No external dependencies
 * - TypeScript-friendly interfaces
 * - Uses SCSS for styling (no inline CSS generation)
 * 
 * @version 3.0.0
 * @author Zubduq 
 */

/**
 * Toast types with their icons
 */
const TOAST_TYPES = {
  success: '✓',
  error: '✕',
  warning: '⚠',
  info: 'ℹ'
};

/**
 * Default configuration for toast notifications
 */
const DEFAULT_CONFIG = {
  duration: 4000,
  closeable: true,
  position: 'top-right',
  maxToasts: 5,
  animationDuration: 400
};

/**
 * Toast notification manager class
 * Handles creation, display, and lifecycle of toast notifications
 */
export class ToastManager {
  /**
   * Initialize the toast manager
   * @param {Object} config - Configuration options
   */
  constructor(config = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.container = null;
    this.toasts = new Set();
    this.isInitialized = false;
    
    // Track timeouts for proper cleanup (MEMORY MANAGEMENT)
    this.timeouts = new Map(); // Map toast element to timeout ID
    
    // Bind methods to maintain context
    this.show = this.show.bind(this);
    this.success = this.success.bind(this);
    this.error = this.error.bind(this);
    this.warning = this.warning.bind(this);
    this.info = this.info.bind(this);
    this.clear = this.clear.bind(this);
    this.remove = this.remove.bind(this);
    
    // Initialize when DOM is ready
    this._initialize();
    
    // Track active timeouts for debugging (development only)
    if (process.env.NODE_ENV === 'development') {
      this._logTimeoutStats = () => {
        console.log(`🔍 Toast Timeouts: ${this.timeouts.size} active, ${this.toasts.size} toasts`);
      };
    }
  }

  /**
   * Initialize the toast system
   * @private
   */
  _initialize() {
    if (this.isInitialized) return;
    
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this._setupContainer());
    } else {
      this._setupContainer();
    }
  }

  /**
   * Set up the toast container
   * @private
   */
  _setupContainer() {
    try {
      this._createContainer();
      this.isInitialized = true;
    } catch (error) {
      // Silent initialization failure
    }
  }

  /**
   * Create the toast container element
   * @private
   */
  _createContainer() {
    // Remove existing container if present
    const existingContainer = document.getElementById('toast-container');
    if (existingContainer) {
      existingContainer.remove();
    }

    this.container = document.createElement('div');
    this.container.id = 'toast-container';
    this.container.className = `toast-container toast-${this.config.position}`;
    this.container.setAttribute('aria-live', 'polite');
    this.container.setAttribute('aria-atomic', 'true');
    
    document.body.appendChild(this.container);
  }

  /**
   * Display a toast notification
   * @param {string} message - Toast message
   * @param {string} [type='info'] - Toast type (success, error, warning, info)
   * @param {Object} [config] - Configuration options
   * @returns {HTMLElement} Created toast element
   */
  show(message, type = 'info', config = {}) {
    if (!this.isInitialized) {
      return null;
    }

    if (!message || typeof message !== 'string') {
      return null;
    }

    const toastConfig = { ...this.config, ...config };
    const icon = TOAST_TYPES[type] || TOAST_TYPES.info;
    
    // Limit number of toasts
    this._enforceMaxToasts();
    
    const toast = this._createToastElement(message, type, icon, toastConfig);
    this._addToastToContainer(toast, toastConfig.duration);
    
    return toast;
  }

  /**
   * Show success toast
   * @param {string} message - Toast message
   * @param {Object} [config] - Configuration options
   * @returns {HTMLElement} Created toast element
   */
  success(message, config = {}) {
    return this.show(message, 'success', { ...config, duration: config.duration || 4000 });
  }

  /**
   * Show error toast
   * @param {string} message - Toast message
   * @param {Object} [config] - Configuration options
   * @returns {HTMLElement} Created toast element
   */
  error(message, config = {}) {
    return this.show(message, 'error', { ...config, duration: config.duration || 5000 });
  }

  /**
   * Show warning toast
   * @param {string} message - Toast message
   * @param {Object} [config] - Configuration options
   * @returns {HTMLElement} Created toast element
   */
  warning(message, config = {}) {
    return this.show(message, 'warning', { ...config, duration: config.duration || 4000 });
  }

  /**
   * Show info toast
   * @param {string} message - Toast message
   * @param {Object} [config] - Configuration options
   * @returns {HTMLElement} Created toast element
   */
  info(message, config = {}) {
    return this.show(message, 'info', { ...config, duration: config.duration || 4000 });
  }

  /**
   * Remove all toast notifications
   */
  clear() {
    this.toasts.forEach(toast => this.remove(toast));
  }

  /**
   * Remove specific toast notification
   * @param {HTMLElement} toast - Toast element to remove
   */
  remove(toast) {
    if (!toast || !this.toasts.has(toast)) return;
    
    // Clear any associated timeout (CRITICAL FOR MEMORY MANAGEMENT)
    if (this.timeouts.has(toast)) {
      clearTimeout(this.timeouts.get(toast));
      this.timeouts.delete(toast);
    }
    
    this.toasts.delete(toast);
    toast.classList.remove('show');
    
    setTimeout(() => {
      if (toast.parentNode) {
        toast.remove();
      }
    }, this.config.animationDuration);
  }

  /**
   * Enforce maximum number of toasts
   * @private
   */
  _enforceMaxToasts() {
    if (this.toasts.size >= this.config.maxToasts) {
      const oldestToast = Array.from(this.toasts)[0];
      this.remove(oldestToast);
    }
  }

  /**
   * Create toast DOM element
   * @private
   */
  _createToastElement(message, type, icon, config) {
    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'assertive');
    
    // Create icon
    const iconElement = document.createElement('div');
    iconElement.className = 'toast-icon';
    iconElement.textContent = icon;
    
    // Create content
    const content = document.createElement('div');
    content.className = 'toast-content';
    
    const messageElement = document.createElement('p');
    messageElement.className = 'toast-message';
    messageElement.textContent = message;
    content.appendChild(messageElement);
    
    // Create close button
    let closeButton = null;
    if (config.closeable) {
      closeButton = document.createElement('button');
      closeButton.className = 'toast-close';
      closeButton.innerHTML = '×';
      closeButton.setAttribute('aria-label', 'Close notification');
      closeButton.addEventListener('click', () => this.remove(toast));
    }
    
    // Assemble toast
    toast.appendChild(iconElement);
    toast.appendChild(content);
    if (closeButton) {
      toast.appendChild(closeButton);
    }
    
    // Add progress bar for auto-dismiss toasts
    if (config.duration > 0) {
      const progress = document.createElement('div');
      progress.className = 'toast-progress';
      progress.style.width = '100%';
      toast.appendChild(progress);
      
      // Animate progress bar
      requestAnimationFrame(() => {
        progress.style.width = '0%';
        progress.style.transition = `width ${config.duration}ms linear`;
      });
    }
    
    return toast;
  }

  /**
   * Add toast to container with animations
   * @private
   */
  _addToastToContainer(toast, duration) {
    this.toasts.add(toast);
    this.container.appendChild(toast);
    
    // Trigger show animation
    requestAnimationFrame(() => {
      toast.classList.add('show');
    });
    
    // Auto-remove after duration with timeout tracking (MEMORY MANAGEMENT)
    if (duration > 0) {
      const timeoutId = setTimeout(() => {
        this.remove(toast);
      }, duration);
      
      // Store timeout ID for cleanup
      this.timeouts.set(toast, timeoutId);
    }
  }

  /**
   * Destroy the toast manager and clean up resources
   */
  destroy() {
    // Clear all pending timeouts (CRITICAL FOR MEMORY MANAGEMENT)
    this.timeouts.forEach((timeoutId, toast) => {
      clearTimeout(timeoutId);
    });
    this.timeouts.clear();
    
    this.clear();
    
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
    
    this.toasts.clear();
    this.isInitialized = false;
  }

  /**
   * Cleanup method for proper memory management
   * Alias for destroy() to match other components
   */
  cleanup() {
    this.destroy();
  }
}

// Create and export singleton instance
let toastInstance = null;

/**
 * Get the singleton toast manager instance
 * @param {Object} [config] - Configuration options
 * @returns {ToastManager} Toast manager instance
 */
export function getToast(config = {}) {
  if (!toastInstance) {
    toastInstance = new ToastManager(config);
  }
  return toastInstance;
}

/**
 * Show a toast notification
 * @param {string} message - Toast message
 * @param {string} [type='info'] - Toast type
 * @param {Object} [config] - Configuration options
 * @returns {HTMLElement} Created toast element
 */
export function showToast(message, type = 'info', config = {}) {
  // Log to console for debugging
  const logMap = {
    success: 'log',
    error: 'error',
    warning: 'warn',
    info: 'info'
  };
  const logMethod = logMap[type] || 'log';
  console[logMethod](`[TOAST ${type.toUpperCase()}] ${message}`);
  
  return getToast().show(message, type, config);
}

/**
 * Show success toast
 * @param {string} message - Toast message
 * @param {Object} [config] - Configuration options
 * @returns {HTMLElement} Created toast element
 */
export function showSuccess(message, config = {}) {
  console.log(`[TOAST SUCCESS] ${message}`);
  return getToast().success(message, config);
}

/**
 * Show error toast
 * @param {string} message - Toast message
 * @param {Object} [config] - Configuration options
 * @returns {HTMLElement} Created toast element
 */
export function showError(message, config = {}) {
  console.error(`[TOAST ERROR] ${message}`);
  return getToast().error(message, config);
}

/**
 * Show warning toast
 * @param {string} message - Toast message
 * @param {Object} [config] - Configuration options
 * @returns {HTMLElement} Created toast element
 */
export function showWarning(message, config = {}) {
  console.warn(`[TOAST WARNING] ${message}`);
  return getToast().warning(message, config);
}

/**
 * Show info toast
 * @param {string} message - Toast message
 * @param {Object} [config] - Configuration options
 * @returns {HTMLElement} Created toast element
 */
export function showInfo(message, config = {}) {
  console.info(`[TOAST INFO] ${message}`);
  return getToast().info(message, config);
}

/**
 * Clear all toasts
 */
export function clearToasts() {
  if (toastInstance) {
    toastInstance.clear();
  }
}

/**
 * Cleanup toast system for memory management
 * Destroys singleton instance and clears all timeouts
 */
export function cleanupToasts() {
  if (toastInstance) {
    toastInstance.destroy();
    toastInstance = null;
  }
}

/**
 * Debug function to check toast timeout stats
 * Available in development mode only
 */
export function getToastStats() {
  if (toastInstance && process.env.NODE_ENV === 'development') {
    return {
      activeToasts: toastInstance.toasts.size,
      activeTimeouts: toastInstance.timeouts.size,
      isInitialized: toastInstance.isInitialized
    };
  }
  return null;
}

// For backward compatibility in non-module environments
if (typeof window !== 'undefined') {
  // Create global instance for legacy code
  window.Toast = getToast();
  
  // Provide global functions
  window.showToast = showToast;
  window.showSuccess = showSuccess;
  window.showError = showError;
  window.showWarning = showWarning;
  window.showInfo = showInfo;
  window.clearToasts = clearToasts;
  window.cleanupToasts = cleanupToasts;
  
  // Development debugging
  if (process.env.NODE_ENV === 'development') {
    window.getToastStats = getToastStats;
  }
  
  // Register with memory leak prevention system if available
  if (window.memoryLeakPrevention && typeof window.memoryLeakPrevention.registerComponentCleanup === 'function') {
    window.memoryLeakPrevention.registerComponentCleanup('ToastSystem', cleanupToasts);
  }
}
